import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay.tsx";
import Flex from "common/Boxes/Flex.tsx";
import Segmented from "common/Segmented/Segmented.tsx";
import { SubscriptionPaymentMethod } from "@shared/features/settings/subscription/types.ts";
import { PaymentMethod, SubscriptionPaymentMethodType } from "@shared/types";
import CloseOutlined from "@ant-design/icons/CloseOutlined";
import React, { useContext, useEffect, useMemo } from "react";
import { useIsSmallMobileCheckout } from "features/settings/common/hooks.tsx";
import styled, { useTheme } from "styled-components";
import { FormInstance } from "antd/es/form";
import { IS_WEB_VIEW } from "common/constants.ts";
import { BuySubscriptionContext } from "features/settings/Subscription/BuySubscription.context.tsx";
import { translateOptionName } from "@shared/common/i18n/optionNames";

interface PaymentMethodsCheckoutProps {
  form: FormInstance;
  checkoutContent?: React.JSX.Element;
  hideCreditCard?: boolean;
}

const PaymentMethodsCheckout = ({
  form,
  checkoutContent,
  hideCreditCard,
}: PaymentMethodsCheckoutProps) => {
  const {
    paymentMethod,
    setPaymentMethod,
    subscriptionPaymentMethod,
    setSubscriptionPaymentMethod,
    paymentMethods,
    isLoadingPaymentMethods,
    isCryptoPayment,
    previousStep,
    subscriptionParams,
  } = useContext(BuySubscriptionContext)!;
  const queryPaymentMethod = subscriptionParams.paymentMethod;
  const isSmallMobile = useIsSmallMobileCheckout();
  const theme = useTheme();

  const findPaymentMethod = (
    paymentOptionName?: string,
    paymentOptionId?: SubscriptionPaymentMethodType
  ) => {
    return paymentMethods?.find(
      (paymentMethod) =>
        paymentMethod.payment_option.toLowerCase() ===
          paymentOptionName?.toLowerCase() ||
        paymentMethod.id === paymentOptionId
    );
  };

  const getDefaultPaymentMethod = () => {
    let defaultPaymentMethod: PaymentMethod | undefined;
    if (queryPaymentMethod) {
      defaultPaymentMethod = findPaymentMethod(queryPaymentMethod);
    }
    if (!defaultPaymentMethod) {
      defaultPaymentMethod = findPaymentMethod(
        undefined,
        isCryptoPayment
          ? DEFAULT_CRYPTO_METHOD
          : SubscriptionPaymentMethodType.PAYPAL
      );
    }
    return defaultPaymentMethod || paymentMethods?.[0];
  };

  useEffect(() => {
    if (paymentMethods && !paymentMethod) {
      const defaultPaymentMethod = getDefaultPaymentMethod();
      setPaymentMethod(defaultPaymentMethod);
      setSubscriptionPaymentMethod(
        defaultPaymentMethod?.id !== SubscriptionPaymentMethodType.PAYPAL
          ? SubscriptionPaymentMethod.CRYPTO
          : SubscriptionPaymentMethod.PAYPAL
      );
    }
  }, [paymentMethods, paymentMethod]);

  useEffect(() => {
    if (
      paymentMethod &&
      paymentMethod?.id !== SubscriptionPaymentMethodType.PAYPAL
    ) {
      form.setFieldsValue({ network: paymentMethod?.id });
    }
  }, [paymentMethod?.id]);

  const paymentMethodsOptions = useMemo(() => {
    let options = Object.values(SubscriptionPaymentMethod);
    if (hideCreditCard) {
      options = options.filter(
        (paymentMethod) =>
          paymentMethod !== SubscriptionPaymentMethod.CREDIT_CARD
      );
    }
    return options;
  }, [hideCreditCard]);

  // New look: the drawer's close button (on the title row) is used instead.
  const showCloseButton = isSmallMobile && !!previousStep && !theme.isNewLook;

  return (
    <LoadingSpinnerOverlay
      isLoading={isLoadingPaymentMethods}
      hideContentWhileLoading={true}
      spinnerMarginTop={100}
    >
      <Flex style={{ marginBottom: 16 }}>
        {!IS_WEB_VIEW && (
          <StyledSegment
            options={paymentMethodsOptions.map((option) => ({
              value: option,
              label: translateOptionName(option),
            }))}
            onChange={(value) => {
              const subscriptionPaymentMethod =
                value as SubscriptionPaymentMethod;
              setSubscriptionPaymentMethod(subscriptionPaymentMethod);
              setPaymentMethod(
                findPaymentMethod(
                  undefined,
                  subscriptionPaymentMethod ===
                    SubscriptionPaymentMethod.PAYPAL ||
                    subscriptionPaymentMethod ===
                      SubscriptionPaymentMethod.CREDIT_CARD
                    ? SubscriptionPaymentMethodType.PAYPAL
                    : DEFAULT_CRYPTO_METHOD
                )
              );
            }}
            value={subscriptionPaymentMethod}
            block={true}
            style={{ width: "100%" }}
          />
        )}
        {showCloseButton && (
          <CloseOutlined
            onClick={previousStep}
            style={{
              position: "absolute",
              marginBottom: 24,
              right: 16,
              color: theme.colors.textColor,
            }}
          />
        )}
      </Flex>
      {checkoutContent}
    </LoadingSpinnerOverlay>
  );
};

export default PaymentMethodsCheckout;

const DEFAULT_CRYPTO_METHOD = SubscriptionPaymentMethodType.USDT_ERC20;

const StyledSegment = styled(Segmented)`
  .ant-segmented-item {
    flex: none !important;
    flex-grow: 1 !important;
  }
`;
